// Система логирования для диагностики проблем авторизации
import { isStoragePersistent, sessionGet, storageRemove } from './storage';

export interface LogEntry {
  timestamp: number;
  level: 'info' | 'warning' | 'error' | 'success';
  message: string;
  details?: any;
}

class AuthLogger {
  private logs: LogEntry[] = [];
  private maxLogs = 100;
  private storageKey = 'ssmm-auth-logs';

  constructor() {
    // Загружаем логи из localStorage при инициализации
    this.loadLogs();
  }

  private loadLogs() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        this.logs = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Failed to load logs from storage:', error);
    }
  }

  private saveLogs() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.logs));
    } catch (error) {
      console.error('Failed to save logs to storage:', error);
    }
  }

  private addLog(level: LogEntry['level'], message: string, details?: any) {
    const entry: LogEntry = {
      timestamp: Date.now(),
      level,
      message,
      details,
    };

    this.logs.push(entry);

    // Ограничиваем количество логов
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    this.saveLogs();

    // Также выводим в консоль для разработки
    const consoleMethod = level === 'error' ? 'error' : level === 'warning' ? 'warn' : 'log';
    console[consoleMethod](`[AUTH ${level.toUpperCase()}]`, message, details || '');
  }

  info(message: string, details?: any) {
    this.addLog('info', message, details);
  }

  warning(message: string, details?: any) {
    this.addLog('warning', message, details);
  }

  error(message: string, details?: any) {
    this.addLog('error', message, details);
  }

  success(message: string, details?: any) {
    this.addLog('success', message, details);
  }

  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  clearLogs() {
    this.logs = [];
    storageRemove(this.storageKey);
  }

  // Получить логи в текстовом формате для копирования
  getLogsAsText(): string {
    return this.logs.map(log => {
      const date = new Date(log.timestamp).toLocaleString('ru-RU');
      let text = `[${date}] [${log.level.toUpperCase()}] ${log.message}`;
      if (log.details) {
        text += `\n  Детали: ${JSON.stringify(log.details, null, 2)}`;
      }
      return text;
    }).join('\n\n');
  }

  // Получить системную информацию для диагностики
  getSystemInfo(): Record<string, any> {
    const info = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screenSize: `${window.screen.width}x${window.screen.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
      isPopup: window.opener !== null,
      isIframe: window.parent !== window,
      protocol: window.location.protocol,
      // Не `!!window.localStorage`: при «Блокировать все cookie» само обращение бросает
      // SecurityError. Проверяем реальной пробной записью — заодно ловим приватный режим.
      storagePersistent: isStoragePersistent(),
      hasCrypto: !!window.crypto,
      url: window.location.href,
      returnUrl: new URLSearchParams(window.location.search).get('return_url') || 
                 sessionGet('ssmm-return-url') || 'не указан',
    };

    return info;
  }

  // Получить полный отчет для отправки в поддержку
  getFullReport(): string {
    const systemInfo = this.getSystemInfo();
    const logs = this.getLogsAsText();

    return `
=== ОТЧЕТ О ПРОБЛЕМЕ АВТОРИЗАЦИИ ===

СИСТЕМНАЯ ИНФОРМАЦИЯ:
${Object.entries(systemInfo).map(([key, value]) => `${key}: ${value}`).join('\n')}

ЛОГИ АВТОРИЗАЦИИ:
${logs || 'Логи отсутствуют'}

=== КОНЕЦ ОТЧЕТА ===
    `.trim();
  }
}

// Экспортируем единственный экземпляр логгера
export const authLogger = new AuthLogger();

